import { useState, useMemo } from 'react';
import { Search, Filter, Download, AlertCircle } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { DataTable } from '../components/tables/DataTable';
import { cn } from '../lib/cn';
import { useAdminAuditLogs, formatRelativeTime } from '../api/hooks';
import type { AuditLogEntry } from '../api/types';
import type { ColumnDef } from '@tanstack/react-table';

function getActionBadge(action: string) {
  const type = action.split('.')[0];
  const badgeClass = {
    user: 'badge-active',
    load: 'badge-pending',
    expense: 'badge-stale',
    invoice: 'badge-pending',
    receipt: 'badge-stale',
    admin: 'badge-suspended',
    business: 'badge-active',
  }[type] || 'badge-stale';

  return (
    <span className={cn('badge', badgeClass)}>
      {action}
    </span>
  );
}

function getTargetDisplay(entry: AuditLogEntry) {
  if (entry.target_type && entry.target_id) {
    return (
      <span className="font-mono text-body-sm text-text-secondary">
        {entry.target_type}:{entry.target_id.slice(0, 8)}...
      </span>
    );
  }
  return (
    <span className="text-text-muted">-</span>
  );
}

const columns: ColumnDef<AuditLogEntry>[] = [
  {
    accessorKey: 'created_at',
    header: 'Time',
    cell: ({ row }) => (
      <span className="text-text-secondary tabular-nums">
        {formatRelativeTime(row.original.created_at)}
      </span>
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ row }) => getActionBadge(row.original.action),
  },
  {
    accessorKey: 'user_email',
    header: 'Actor',
    cell: ({ row }) => (
      <span className="text-text">
        {row.original.user_email}
      </span>
    ),
  },
  {
    accessorKey: 'target_type',
    header: 'Target',
    cell: ({ row }) => getTargetDisplay(row.original),
  },
  {
    accessorKey: 'ip_address',
    header: 'IP Address',
    cell: ({ row }) => (
      <span className="font-mono text-body-sm text-text-muted">
        {row.original.ip_address || '-'}
      </span>
    ),
  },
];

function TableSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-6 w-24" />
          <Skeleton className="h-6 w-28" />
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-6 w-24" />
        </div>
      ))}
    </div>
  );
}

/**
 * Audit log page for tracking all platform activity.
 */
export function AuditPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useMemo(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data, isLoading, error } = useAdminAuditLogs(
    debouncedSearch ? { search: debouncedSearch, limit: 50 } : { limit: 50 }
  );

  return (
    <>
      <Header
        title="Audit Log"
        description={data ? `${data.total_count} entries` : 'Track all platform activity'}
        actions={
          <Button variant="secondary" size="sm">
            <Download size={16} />
            Export
          </Button>
        }
      />

      <div className="p-6">
        {/* Error State */}
        {error && (
          <Card padding="md" hover={false} className="mb-4 border-loss/50">
            <div className="flex items-center gap-3 text-loss">
              <AlertCircle size={20} />
              <span className="text-body-sm">
                Failed to load audit logs. Please check your connection and try again.
              </span>
            </div>
          </Card>
        )}

        {/* Search and Filter Bar */}
        <Card padding="sm" hover={false} className="mb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted"
              />
              <input
                type="text"
                placeholder="Search by action, email, or target..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-9 pr-3 py-2 rounded-button border text-body-sm',
                  'bg-surface',
                  'border-border',
                  'text-text',
                  'placeholder:text-text-muted',
                  'focus:outline-none focus:ring-2 focus:ring-action focus:border-transparent'
                )}
              />
            </div>
            <Button variant="secondary" size="sm">
              <Filter size={16} />
              Filter
            </Button>
          </div>
        </Card>

        {/* Audit Log Table */}
        <Card padding="none" hover={false}>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <DataTable
              data={data?.entries ?? []}
              columns={columns}
              enablePagination
              pageSize={20}
              emptyMessage={searchQuery ? 'No entries match your search' : 'No audit entries yet'}
            />
          )}
        </Card>

        {/* Pagination Info */}
        {data && data.total_count > 0 && (
          <div className="mt-4 text-caption text-text-muted text-center">
            Showing {Math.min(data.entries.length, data.limit)} of {data.total_count} entries
          </div>
        )}
      </div>
    </>
  );
}
